import './Tasktable.style.less'

import {
  BlockOutlined,
  ExportOutlined,
  FilterFilled,
  UndoOutlined,
} from '@ant-design/icons'
import { Button, Empty, Input, Table } from 'antd'
import { useEffect, useState } from 'react'

import cx from 'classnames'
import { isDark } from '@/utils/darkmode'
import {
  concat,
  debounce,
  isEmpty,
  isString,
  map,
  omitBy,
  pick,
  size,
  sortBy,
  toNumber,
  uniqBy,
} from 'lodash'
import { themeAtom } from '@/jotai/theme.jotai'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  dateRangConverter,
  getUrlParams,
  handleAdvanceFilterQueryParams,
} from '@/utils/common'
import { useNavigate } from 'react-router-dom'
import {
  getLocalTaskPageSize,
  nestedTaskColumns,
  storeLocalTaskPageSize,
  transformData,
} from './TaskTable.transform'
import {
  getTaskAssignees,
  getTaskPriorities,
  getTaskSections,
  getTaskStatuses,
  useTaskTable,
} from './TaskTable.hook'
import MaskUpdateTasksDrawer from '../components/MaskUpdateTasksDrawer/MaskUpdateTasksDrawer'

import {
  TASK_PRIORITY_STATUS,
  TASK_ADVANCE_FILTER_LISTING,
  TASK_PAYLOAD_TYPE,
  TASK_STATUSES,
  DEFAULT_TASK_FILTER,
  TASK_PARAMS_TYPE,
  TASK_QUERY_PARAM_TYPE,
  TASK_QUERY_KEYS,
} from '@/constant/task'
import { TablePaginationConfig } from 'antd/es/table'
import { useQuery } from 'react-query'
import DrawerAdvanceFilter from '@/screens/QSF/Inspection/ListInspection/components/DrawerAdvanceFilter'
import { useAuth } from '@/providers/auth.provider'
import { IsAdminRole, IsStandardRole } from '@/utils/users'
import { getStyleIdsByFilter } from '@/screens/Order/Pages/Landing/components/ModalMassUpdate/Task.service'
import { useExportData } from '@/hook/useExportData'
import { ExportConfirmModal } from '@/components/ExportConfirmModal/ExportConfirmModal'

const filterIcon = <FilterFilled style={{ fontSize: '19px' }} />
const undoIcon = <UndoOutlined style={{ fontSize: '19px' }} />
interface TaskTableProps {
  refetchSummary?: () => void
  onFilterChange?: (data: TASK_PAYLOAD_TYPE) => void
}

function TaskTable(props: TaskTableProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const cachePageSize = getLocalTaskPageSize()

  const isAdmin = IsAdminRole(user)
  const isStandard = IsStandardRole(user)
  const isShowingAdvanceFilter = isAdmin || isStandard

  const { refetchSummary, onFilterChange } = props
  const getFilterTilePrams = getUrlParams('filter') || 'all'
  const getPageNumber = toNumber(getUrlParams('page_number'))
  const getSorting = getUrlParams('sort')

  const [selectedTask, setSelectedTask] = useState(null)
  const [isOpenAdvanceFilter, setIsOpenAdvanceFilter] = useState(false)
  const [stateParams, setStateParams] = useState<TASK_PARAMS_TYPE>({
    page_size: cachePageSize,
    page_number: getPageNumber,
    filter: getFilterTilePrams,
    sort: getSorting,
  })
  const [stateFilterTask, setStateFilterTask] = useState<TASK_PAYLOAD_TYPE>({})

  const [theme] = useAtom(themeAtom)

  const initTask = {
    key: '',
    properties: [],
    taskIds: [],
  }
  const [taskIds, setTaskIds] = useState(initTask)

  const [isMassUpdate, setIsMassUpdate] = useState(false)

  const [tmpSearch, setTmpSearch] = useState(getUrlParams('search') ?? '')

  const { data, isLoading, refetch } = useTaskTable(
    stateParams,
    stateFilterTask
  )

  const exportData = useExportData({
    exportKey: 'tasks',
    queryKey: TASK_QUERY_KEYS.MYTASK_EXPORT_SETTING,
  })

  const isEmptyFiltering =
    !size(omitBy(stateFilterTask, isEmpty)) && !size(stateFilterTask.search)

  const { data: dataTaskPriorities } = useQuery(
    ['GET_TASK_PRIORITIES', isOpenAdvanceFilter],
    async () => {
      if (!isOpenAdvanceFilter) {
        return []
      }
      const fetchTaskPriorities = await getTaskPriorities()
      const result = map(fetchTaskPriorities, (item: string) => {
        return {
          value: item,
          text: TASK_PRIORITY_STATUS[item as any],
        }
      })

      return result ?? []
    }
  )
  const { data: dataTaskStatus } = useQuery(
    ['GET_TASK_STATUSES', isOpenAdvanceFilter],
    async () => {
      if (!isOpenAdvanceFilter) {
        return []
      }
      const fetchTaskStatus = await getTaskStatuses()
      const result = map(fetchTaskStatus, (item) => {
        let displayValue: any = item
        let displayText = TASK_STATUSES[item]

        if (+displayValue) {
          displayText = `${item}%`
        }

        return {
          value: displayValue,
          text: displayText,
        }
      })
      return result ?? []
    }
  )

  const { data: dataTaskAssignees } = useQuery(
    ['GET_TASK_ASSIGNEES', isOpenAdvanceFilter],
    async () => {
      if (!isOpenAdvanceFilter) {
        return []
      }
      const defaultAutoFilterAssignee = {
        first_name: user.first_name,
        last_name: user.last_name,
        id: user.id,
      }

      const fetchTaskAssignee = await getTaskAssignees()
      const wrapAssigneeWithUser = sortBy(
        uniqBy(concat(fetchTaskAssignee, defaultAutoFilterAssignee), 'id'),
        ['first_name', 'last_name']
      )

      const result = map(wrapAssigneeWithUser, (item) => ({
        ...item,
        value: item.id,
        text: `${item.first_name} ${item.last_name}`,
      }))
      return result ?? []
    }
  )

  const { data: dataProductDescriptions } = useQuery(
    ['GET_TASK_PRODUCT_DESCRIPTION', isOpenAdvanceFilter],
    async () => {
      if (!isOpenAdvanceFilter) return ''
      return stateFilterTask.product_description || ''
    }
  )

  const { data: dataTaskSections } = useQuery({
    queryKey: ['GET_TASK_SECTIONS', isOpenAdvanceFilter],
    queryFn: async () => {
      const fetchTaskSections = await getTaskSections()
      return map(fetchTaskSections?.options, (item) => ({
        value: item.name,
        label: item.name,
      }))
    },
    enabled: isOpenAdvanceFilter,
  })

  // TODO: implement pagination later
  const { data: dataStyleIds } = useQuery({
    queryKey: ['GET_TASK_STYLE_IDS', isOpenAdvanceFilter],
    queryFn: async () => {
      const params = {
        all: true,
      }
      const data = await getStyleIdsByFilter(params)
      return data?.data?.data
    },
    enabled: isOpenAdvanceFilter,
  })

  const refetchData = () => {
    refetchSummary()
    refetch()
  }

  const onSetPageSize = (size: number) => {
    handleSetStateParams({ page_size: size })
    storeLocalTaskPageSize(size)
  }

  useEffect(() => {
    handleSetStateParams({
      filter: getFilterTilePrams,
    })
    onSetPage(1)
  }, [getFilterTilePrams])

  useEffect(() => {
    handleSetStateParams({
      page_number: getPageNumber,
      sort: getSorting,
    })
  }, [getPageNumber, getSorting])

  useEffect(() => {
    handleAdvanceFilterQueryParams(stateParams, {
      queriesAccepted: TASK_QUERY_PARAM_TYPE,
      navigate,
    })
  }, [stateParams])

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(stateFilterTask)
    }
  }, [stateFilterTask, onFilterChange])

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows) => {
      setTaskIds({
        key: selectedRows[0]?.type,
        properties: [],
        taskIds: selectedRowKeys as string[],
      })
    },
    getCheckboxProps: (record) => ({
      disabled: !record.can_write,
    }),
    selectedRowKeys: taskIds.taskIds,
  }

  const navigate = useNavigate()

  const onSetPage = (page) => {
    handleSetStateParams({ page_number: page })
    setTaskIds(initTask)
  }

  const onSearch = debounce((event) => {
    const getSearchValue = isString(event) ? event : event.target.value
    onSetPage(1)

    handleTableChange({}, { ...stateFilterTask, search: getSearchValue })
    handleSetStateParams({ list_view: null })
  }, 500)

  const handleFetchData = async () => {
    handleFilterParamsUrl()
  }

  useEffect(() => {
    handleFetchData()
  }, [])

  const splitFN = (key) =>
    getUrlParams(key) ? getUrlParams(key).split(',') : null
  // TODO: handle status depend on Tile, currently there is an issues in Drawer when change the tile
  // TODO: Load page with query params need to handle
  const handleFilterParamsUrl = () => {
    const splitPlannedDate: any = splitFN('planned_date')
    const strSearch = splitFN('search')?.toString()
    const getPrams: TASK_PAYLOAD_TYPE = {
      planned_date: splitPlannedDate,
      assignees: splitFN('assignees'),
      priority_statuses: splitFN('priority_statuses'),
      statuses: splitFN('statuses'),
      style_ids: splitFN('style_ids'),
      section_names: splitFN('section_names'),
      product_description: getUrlParams('product_description') || null,
      search: strSearch,
    }

    handleTableChange({ current: stateParams.page_number }, getPrams)
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: any,
    sorter?: any
  ) => {
    const { current = 1 } = pagination
    const [strPlannedDate] = dateRangConverter(filters?.planned_date)

    filters.planned_date = strPlannedDate

    handleAdvanceFilterQueryParams(filters, {
      queriesAccepted: TASK_ADVANCE_FILTER_LISTING,
      navigate,
    })

    let sortColumn = ''

    if (size(sorter)) {
      const { order, columnKey } = sorter
      sortColumn = !size(order)
        ? ''
        : order === 'ascend'
        ? columnKey
        : `-${columnKey}`
    } else {
      sortColumn = stateParams.sort
    }

    handleSetStateParams({ sort: sortColumn })
    onSetPage(current)
    setSelectedTask(null)
    handleSetStateFilters(filters)
  }

  const handleSetStateFilters = (newState: any) => {
    setStateFilterTask((preState) => ({ ...preState, ...newState }))
  }

  const handleSetStateParams = (newState: any) => {
    setStateParams((preState) => ({ ...preState, ...newState }))
  }

  const rowClassName = (record) => {
    return record.id === selectedTask ? 'selected-row' : ''
  }

  const onOpenCloseAdvanceFilterFN = () => {
    setIsOpenAdvanceFilter(!isOpenAdvanceFilter)
    setSelectedTask(null)
  }

  const handleResetFilter = () => {
    handleTableChange({}, DEFAULT_TASK_FILTER)
    setTmpSearch('')
    handleSetStateParams({ list_view: null })
  }

  const handleOnSubmitFilter = (dataFilter) => {
    handleTableChange({}, dataFilter)
    handleSetStateParams({ list_view: null })
  }

  return (
    <>
      <div className="group-order-container">
        <div className="group-order-left">
          <div className="order-search-input-container">
            <Input.Search
              className="task-search-container"
              placeholder="Task, Order No., Style No., Supplier, Factory"
              size="large"
              defaultValue={tmpSearch}
              onChange={onSearch}
            />
            {isShowingAdvanceFilter && (
              <>
                <Button
                  className={`button-advance-filter ${
                    isEmptyFiltering ? 'grey-bg' : ''
                  }`}
                  size="large"
                  type="primary"
                  icon={filterIcon}
                  onClick={onOpenCloseAdvanceFilterFN}
                />
                <Button
                  className="button-clear-filter"
                  disabled={isEmptyFiltering}
                  type="link"
                  icon={undoIcon}
                  onClick={handleResetFilter}
                >
                  Clear
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="task-table-actions">
          {exportData.exportConfigs?.map((item) => (
            <Button
              key={item.name}
              className="export-excel-button"
              size="large"
              icon={<ExportOutlined />}
              onClick={() => exportData.handleExportClick(item)}
              loading={exportData.isExporting}
            >
              {item.title}
            </Button>
          ))}
          <Button
            disabled={taskIds?.taskIds?.length < 2}
            type="primary"
            size="large"
            icon={<BlockOutlined />}
            style={{ marginLeft: 24 }}
            onClick={() => setIsMassUpdate(true)}
          >
            Mass Update
          </Button>
        </div>
      </div>
      <div style={{ marginTop: 24, minHeight: 500 }}>
        <div
          className={cx(
            isDark(theme) && 'dark-table',
            'table-container table-left task-table-container'
          )}
        >
          <Table
            loading={isLoading}
            rowKey="id"
            rowSelection={rowSelection}
            dataSource={transformData(data)}
            columns={nestedTaskColumns([], t, {
              refetch: refetchData,
              setSelectedTask,
              selectedTask,
              queryParams: stateParams,
            })}
            scroll={{ x: 'max-content' }}
            tableLayout="auto"
            onChange={(pagination, filters, sorter) => {
              // TODO: using filters when implement column filter. Now just tmp use stateFilterTask
              handleTableChange(pagination, stateFilterTask, sorter)
            }}
            rowClassName={rowClassName}
            pagination={{
              pageSize: stateParams.page_size,
              current: data?.data?.pagination?.page_number,
              total: data?.data?.pagination?.total_record,
              onChange: (page, pageSize) => {
                onSetPage(page)
                onSetPageSize(pageSize)
              },
              showSizeChanger: true,
              showLessItems: true,
            }}
            locale={{
              emptyText: (() => {
                const tasks = transformData(data)
                if (tasks.length === 0 && !isLoading) {
                  if (getUrlParams('list_view') === 'all') {
                    return (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                            }}
                          >
                            <span>No tasks assigned.</span>
                            <Button
                              className="button-all-tasks"
                              type="link"
                              onClick={handleResetFilter}
                            >
                              View All Tasks
                            </Button>
                          </div>
                        }
                      />
                    )
                  }
                  return (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No tasks found."
                    />
                  )
                }
                return null
              })(),
            }}
          />
        </div>
      </div>
      <DrawerAdvanceFilter
        isOpen={isOpenAdvanceFilter}
        onClose={onOpenCloseAdvanceFilterFN}
        dataSource={{
          statuses: dataTaskStatus,
          priority_statuses: dataTaskPriorities,
          assignees: dataTaskAssignees,
          product_description: dataProductDescriptions,
          style_ids: dataStyleIds?.data,
          section_names: dataTaskSections,
        }}
        dataFilter={pick(stateFilterTask, TASK_ADVANCE_FILTER_LISTING)}
        onSubmit={handleOnSubmitFilter}
        options={{
          customTitle: {
            statuses: {
              label: 'Task Status',
            },
          },
        }}
        drawerMode="myTasks"
      />

      <MaskUpdateTasksDrawer
        onClose={() => setIsMassUpdate(false)}
        open={isMassUpdate}
        tasks={taskIds}
        taskType={taskIds.key}
        refetch={refetchData}
      />

      <ExportConfirmModal
        open={exportData.isModalOpen}
        onClose={exportData.closeModal}
        onConfirm={exportData.handleConfirmExport}
        title={exportData.selectedItem?.title || 'Export Data'}
        loading={exportData.isExporting}
        error={exportData.exportError}
      />
    </>
  )
}

export default TaskTable
